import {Body1, Body2, Headline3, Headline4} from '@ui/atoms/typography';
import {ToggleSwitch} from '@ui/atoms/toggle-switch';
import {useNotification} from './hooks/useNotification';
import {
  ChannelCard,
  ChannelHeader,
  ChannelHeading,
  GroupTitle,
  NotificationPage,
  PageIntro,
  PreferenceCopy,
  PreferenceGroup,
  PreferenceGroups,
  PreferenceRow,
  PreferenceStatus,
} from './notification.styles';
import {
  buildEmailPreferenceSections,
  PreferenceDefinition,
} from './notification-preferences';

const preferenceSwitchIds: Record<string, number> = {
  email: 100,
  checkinReminder: 102,
  checkinOverdueReminder: 103,
  checkinSubmitted: 104,
  goalUpdate: 107,
  goalEdit: 108,
};

const NotificationSettings = () => {
  const {
    draft,
    isAdmin,
    isCheckinsEnabled,
    isGoalsEnabled,
    isSaving,
    saveError,
    hasSaved,
    checkinSubmissionParameter,
    toggleChannel,
    toggleParameter,
  } = useNotification();

  const emailSections = buildEmailPreferenceSections({
    checkinSubmissionParameter,
    isAdmin,
    isCheckinsEnabled,
    isGoalsEnabled,
  });

  const renderPreference = (preference: PreferenceDefinition) => {
    const isChecked = draft.email.parameters.includes(preference.parameter);
    const isDisabled = !draft.email.enabled || isSaving;

    return (
      <PreferenceRow key={preference.id} disabled={isDisabled}>
        <PreferenceCopy>
          <div>
            <Body2 kind="textDark" weight="semibold">
              {preference.title}
            </Body2>
          </div>
          <Body2 kind="textBody">{preference.description}</Body2>
        </PreferenceCopy>
        <ToggleSwitch
          switchId={preferenceSwitchIds[preference.id]}
          checked={isChecked}
          disabled={isDisabled}
          aria-label={`${preference.title} ${
            isChecked ? 'enabled' : 'disabled'
          }`}
          onChange={() => toggleParameter('email', preference.parameter)}
        />
      </PreferenceRow>
    );
  };

  return (
    <NotificationPage>
      <PageIntro>
        <Headline3 kind="blackTwo">Notification settings</Headline3>
        <Body2 kind="textBody">
          Choose which activity Beam should also send to your email.
        </Body2>
      </PageIntro>

      <ChannelCard>
        <ChannelHeader>
          <ChannelHeading>
            <Headline4 kind="blackTwo">Email notifications</Headline4>
            <Body2 kind="textBody">
              Receive selected Beam activity in your inbox.
            </Body2>
            <PreferenceStatus
              aria-live={saveError ? 'assertive' : 'polite'}
              role={saveError ? 'alert' : 'status'}>
              <Body2 kind={saveError ? 'red400' : 'textMuted'}>
                {saveError ||
                  (isSaving
                    ? 'Saving…'
                    : hasSaved
                    ? 'Saved'
                    : 'Changes save automatically.')}
              </Body2>
            </PreferenceStatus>
          </ChannelHeading>
          <ToggleSwitch
            switchId={preferenceSwitchIds.email}
            checked={draft.email.enabled}
            disabled={isSaving}
            aria-label={`Email notifications ${
              draft.email.enabled ? 'enabled' : 'disabled'
            }`}
            onChange={() => toggleChannel('email')}
          />
        </ChannelHeader>

        {emailSections.length > 0 && (
          <PreferenceGroups>
            {emailSections.map((section) => (
              <PreferenceGroup key={section.id} aria-labelledby={section.id}>
                <GroupTitle>
                  <Body1 id={section.id} kind="textDark" weight="semibold">
                    {section.title}
                  </Body1>
                  <Body2 kind="textBody">{section.description}</Body2>
                </GroupTitle>
                {section.preferences.map(renderPreference)}
              </PreferenceGroup>
            ))}
          </PreferenceGroups>
        )}
      </ChannelCard>
    </NotificationPage>
  );
};

export default NotificationSettings;
